import { useState, useEffect, useCallback } from 'react'
import { Search, Download, Calendar, ChevronUp, ChevronDown } from 'lucide-react'
import { Card } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DateRangePicker,
} from '../ui'
import { format } from 'date-fns'
import { getTransactionHistory } from '../../api/rewards'

interface BonusPointTransaction {
  id: string
  firstName: string
  lastName: string
  email: string
  employeeEntrataEmail?: string
  points: number
  description: string
  rewardedBy: string
  date: string
  department?: string
  rewardEventStatusId?: number
  rewardEventStatus?: string
}

interface TransactionHistoryProps {
  transactions?: BonusPointTransaction[]
}

type SortField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'employeeEntrataEmail'
  | 'points'
  | 'rewardedBy'
  | 'date'
type SortDirection = 'asc' | 'desc'
type DateFilterType = 'all' | 'current-month' | 'previous-month' | 'year-to-date' | 'custom'

const DATE_FILTER_OPTIONS = {
  ALL: 'all' as DateFilterType,
  CURRENT_MONTH: 'current-month' as DateFilterType,
  PREVIOUS_MONTH: 'previous-month' as DateFilterType,
  YEAR_TO_DATE: 'year-to-date' as DateFilterType,
  CUSTOM: 'custom' as DateFilterType,
} as const

const REWARD_EVENT_STATUS = {
  PENDING: 1,
} as const

const MONTH_MAP: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
}

export function TransactionHistory({ transactions: initialTransactions }: TransactionHistoryProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [displayCount, setDisplayCount] = useState(8)
  const [isLoading, setIsLoading] = useState(false)
  const [sortBy, setSortBy] = useState('date-desc')
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [dateFilter, setDateFilter] = useState<DateFilterType>(DATE_FILTER_OPTIONS.CURRENT_MONTH)
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({})
  const [tempDateRange, setTempDateRange] = useState<{ from?: Date; to?: Date }>({})
  const [transactions, setTransactions] = useState<BonusPointTransaction[]>([])
  const [apiLoading, setApiLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const convertApiTransaction = (apiTransaction: any): BonusPointTransaction => {
    return {
      id: apiTransaction.id.toString(),
      firstName: apiTransaction.firstName,
      lastName: apiTransaction.lastName,
      email: apiTransaction.emailAddress,
      employeeEntrataEmail: apiTransaction.employeeEmail,
      points: apiTransaction.points,
      description: apiTransaction.description,
      rewardedBy: apiTransaction.rewardedByName,
      date: format(new Date(apiTransaction.createdDate), 'MMM d, yyyy, h:mm a'),
      department: apiTransaction.fundedByDepartmentId?.toString(),
      rewardEventStatusId: apiTransaction.rewardEventStatusId,
      rewardEventStatus: apiTransaction.rewardEventStatus,
    }
  }

  const fetchTransactions = useCallback(
    async (dateRange?: { fromDate?: string; toDate?: string }) => {
      setApiLoading(true)
      setApiError(null)

      try {
        const response = await getTransactionHistory(dateRange)
        let transactionsData: any[] = []

        if (Array.isArray(response)) {
          transactionsData = response
        } else {
          setApiError('Invalid response format')
          setTransactions([])
          return
        }

        if (transactionsData.length > 0) {
          const convertedTransactions = transactionsData.map(convertApiTransaction)
          setTransactions(convertedTransactions)
        } else {
          setTransactions([])
        }
      } catch {
        setApiError('Failed to fetch transactions')
        setTransactions([])
      } finally {
        setApiLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (initialTransactions && initialTransactions.length > 0) {
      setTransactions(initialTransactions)
    }
  }, [initialTransactions])

  useEffect(() => {
    let dateRange: { fromDate?: string; toDate?: string } | undefined

    if (dateFilter === DATE_FILTER_OPTIONS.CUSTOM && customDateRange.from) {
      dateRange = {
        fromDate: format(customDateRange.from, 'yyyy-MM-dd'),
        toDate: customDateRange.to
          ? format(customDateRange.to, 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
      }
    }

    fetchTransactions(dateRange)
  }, [dateFilter, customDateRange, fetchTransactions])

  const handleColumnSort = (field: SortField) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      setSortDirection(newDirection)
      setSortBy(`${field}-${newDirection}`)
    } else {
      setSortField(field)
      setSortDirection('asc')
      setSortBy(`${field}-asc`)
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <ChevronUp className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-50 transition-opacity" />
      )
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-3 w-3 text-foreground" />
    ) : (
      <ChevronDown className="h-3 w-3 text-foreground" />
    )
  }

  const isInDateRange = (transactionDate: string, filter: string) => {
    const txDate = new Date(transactionDate)
    const now = new Date()

    switch (filter) {
      case DATE_FILTER_OPTIONS.ALL:
        return true
      case DATE_FILTER_OPTIONS.CURRENT_MONTH:
        return txDate.getMonth() === now.getMonth() && txDate.getFullYear() === now.getFullYear()
      case DATE_FILTER_OPTIONS.PREVIOUS_MONTH: {
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        return (
          txDate.getMonth() === prevMonth.getMonth() &&
          txDate.getFullYear() === prevMonth.getFullYear()
        )
      }
      case DATE_FILTER_OPTIONS.YEAR_TO_DATE:
        return txDate.getFullYear() === now.getFullYear()
      case DATE_FILTER_OPTIONS.CUSTOM: {
        if (!customDateRange.from) return true
        const fromDate = format(customDateRange.from, 'yyyy-MM-dd')
        const toDate = customDateRange.to
          ? format(customDateRange.to, 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd')
        const txDateStr = format(txDate, 'yyyy-MM-dd')
        return txDateStr >= fromDate && txDateStr <= toDate
      }
      default:
        return true
    }
  }

  const sortTransactions = (transactions: BonusPointTransaction[]) => {
    return [...transactions].sort((a, b) => {
      const [field, direction] = sortBy.split('-') as [string, SortDirection]

      const sortFunctions: Record<
        string,
        (a: BonusPointTransaction, b: BonusPointTransaction) => number
      > = {
        date: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        points: (a, b) => b.points - a.points,
        firstName: (a, b) => a.firstName.localeCompare(b.firstName),
        lastName: (a, b) => a.lastName.localeCompare(b.lastName),
        email: (a, b) => a.email.localeCompare(b.email),
        employeeEntrataEmail: (a, b) =>
          (a.employeeEntrataEmail || '').localeCompare(b.employeeEntrataEmail || ''),
        rewardedBy: (a, b) => a.rewardedBy.localeCompare(b.rewardedBy),
      }

      const sortFn = sortFunctions[field] || sortFunctions.date
      return direction === 'desc' ? sortFn(a, b) : sortFn(b, a)
    })
  }

  const filteredTransactions = sortTransactions(
    transactions.filter(transaction => {
      const matchesSearch = [
        transaction.firstName,
        transaction.lastName,
        transaction.email,
        transaction.employeeEntrataEmail,
        transaction.rewardedBy,
        transaction.description,
      ].some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesDate = initialTransactions ? isInDateRange(transaction.date, dateFilter) : true

      return matchesSearch && matchesDate
    })
  )

  const displayedTransactions = filteredTransactions.slice(0, displayCount)
  const hasMoreTransactions = displayCount < filteredTransactions.length

  const handleLoadMore = async () => {
    setIsLoading(true)
    await new Promise(resolve => setTimeout(resolve, 1000))
    setDisplayCount(prev => Math.min(prev + 8, filteredTransactions.length))
    setIsLoading(false)
  }

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    if (range) {
      setTempDateRange(range)
      if (range.from && range.to) {
        setCustomDateRange(range)
        setDateFilter(DATE_FILTER_OPTIONS.CUSTOM)
      }
    }
  }

  const formatDateTimeForCSV = (dateString: string) => {
    const dateTimeParts = dateString.split(' ')
    const shortMonth = dateTimeParts[0]
    const day = dateTimeParts[1].replace(',', '')
    const year = dateTimeParts[2]
    const timePart = dateTimeParts.slice(3).join(' ')

    const monthNumber = MONTH_MAP[shortMonth] || '01'
    const formattedDay = day.padStart(2, '0')

    return `${monthNumber}/${formattedDay}/${year} ${timePart}`
  }

  const exportToCSV = () => {
    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Employee Entrata Email',
      'Department ID',
      'Department Funding Points',
      'Points',
      'Description',
      'Rewarded By',
      'Date and Time',
    ]

    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(transaction => {
        const combinedDateTime = formatDateTimeForCSV(transaction.date)
        return [
          `"${transaction.firstName}"`,
          `"${transaction.lastName}"`,
          `"${transaction.email}"`,
          `"${transaction.employeeEntrataEmail || ''}"`,
          `"${transaction.id}"`,
          `"${transaction.department || ''}"`,
          transaction.points,
          `"${transaction.description}"`,
          `"${transaction.rewardedBy}"`,
          `"${combinedDateTime}"`,
        ].join(',')
      }),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bonus-point-transactions-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const getFilterDisplayText = () => {
    if (dateFilter === DATE_FILTER_OPTIONS.CUSTOM && customDateRange.from) {
      const fromDate = format(customDateRange.from, 'MMM dd, yyyy')
      const toDate = customDateRange.to ? format(customDateRange.to, 'MMM dd, yyyy') : 'Present'
      return `${fromDate} - ${toDate}`
    }
    return ''
  }

  const getSelectDisplayValue = () => {
    const displayValues: Record<DateFilterType, string> = {
      all: 'All',
      'current-month': 'Current Month',
      'previous-month': 'Previous Month',
      'year-to-date': 'Year to Date',
      custom: customDateRange.from
        ? `${format(customDateRange.from, 'MMM dd, yyyy')} - ${
            customDateRange.to ? format(customDateRange.to, 'MMM dd, yyyy') : 'Present'
          }`
        : 'Custom Range',
    }
    return displayValues[dateFilter] || 'Select date range'
  }

  useEffect(() => {
    setDisplayCount(8)
  }, [sortBy, searchTerm, dateFilter, customDateRange])

  useEffect(() => {
    const [field, direction] = sortBy.split('-') as [string, SortDirection]
    setSortField(field as SortField)
    setSortDirection(direction)
  }, [sortBy])

  const renderTableHeader = (field: SortField, label: string, className = '') => (
    <th className={`text-left py-4 px-4 font-medium ${className}`}>
      <button
        onClick={() => handleColumnSort(field)}
        className="group flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
      >
        <span>{label}</span>
        {getSortIcon(field)}
      </button>
    </th>
  )

  const renderTableRow = (transaction: BonusPointTransaction) => {
    return (
      <tr key={transaction.id} className="border-b hover:bg-muted/50">
        <td className="py-4 px-4">{transaction.firstName}</td>
        <td className="py-4 px-4">{transaction.lastName}</td>
        <td className="py-4 px-4">
          <span className="text-sm break-all">{transaction.email}</span>
        </td>
        <td className="py-4 px-4 hidden lg:table-cell">
          <span className="text-sm break-all">
            {transaction.employeeEntrataEmail || (
              <span className="text-muted-foreground italic">-</span>
            )}
          </span>
        </td>
        <td className="py-4 px-4 text-right">
          {transaction.rewardEventStatusId === REWARD_EVENT_STATUS.PENDING ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
              {transaction.rewardEventStatus?.charAt(0).toUpperCase() +
                (transaction.rewardEventStatus?.slice(1).toLowerCase() ?? 'Pending')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
              +{transaction.points}
            </span>
          )}
        </td>
        <td className="py-4 px-4 max-w-xs hidden lg:table-cell">
          <div className="truncate" title={transaction.description}>
            {transaction.description}
          </div>
        </td>
        <td className="py-4 px-4 hidden sm:table-cell">{transaction.rewardedBy}</td>
        <td className="py-4 px-4 text-sm text-muted-foreground">{transaction.date}</td>
      </tr>
    )
  }

  const renderMobileCard = (transaction: BonusPointTransaction) => (
    <div key={transaction.id} className="border rounded-lg p-4 space-y-3">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="font-medium">
            {transaction.firstName} {transaction.lastName}
          </div>
          <div className="text-sm text-muted-foreground break-all">{transaction.email}</div>
        </div>
        <div className="text-right">
          {transaction.rewardEventStatusId === REWARD_EVENT_STATUS.PENDING ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-yellow-100 text-red-800">
              {transaction.rewardEventStatus?.charAt(0).toUpperCase() +
                (transaction.rewardEventStatus?.slice(1).toLowerCase() ?? 'Pending')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
              +{transaction.points}
            </span>
          )}
        </div>
      </div>

      <div className="text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Date:</span>
          <span>{transaction.date}</span>
        </div>
        {transaction.employeeEntrataEmail && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Employee Email:</span>
            <span className="break-all">{transaction.employeeEntrataEmail}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Rewarded By:</span>
          <span>{transaction.rewardedBy}</span>
        </div>
        <div className="mt-2">
          <span className="text-muted-foreground">Description:</span>
          <div className="mt-1 text-sm">{transaction.description}</div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2">Bonus Points Transaction History</h2>
        <p className="text-muted-foreground">
          View and manage all bonus point allocations across your organization
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by name, email, description, or rewarded by..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-80"
            />
          </div>

          <div className="flex gap-2">
            <Select
              value={dateFilter}
              onValueChange={(value: string) => {
                setDateFilter(value as DateFilterType)
                if (value !== DATE_FILTER_OPTIONS.CUSTOM) {
                  setCustomDateRange({})
                  setTempDateRange({})
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-48">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue>{getSelectDisplayValue()}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DATE_FILTER_OPTIONS.ALL}>All</SelectItem>
                <SelectItem value={DATE_FILTER_OPTIONS.CURRENT_MONTH}>Current Month</SelectItem>
                <SelectItem value={DATE_FILTER_OPTIONS.PREVIOUS_MONTH}>Previous Month</SelectItem>
                <SelectItem value={DATE_FILTER_OPTIONS.YEAR_TO_DATE}>Year to Date</SelectItem>
                {dateFilter === DATE_FILTER_OPTIONS.CUSTOM && (
                  <SelectItem value={DATE_FILTER_OPTIONS.CUSTOM}>
                    Custom Range
                    {getFilterDisplayText() && (
                      <span className="text-xs text-muted-foreground ml-2">
                        ({getFilterDisplayText()})
                      </span>
                    )}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            <DateRangePicker
              date={tempDateRange}
              onDateChange={handleDateRangeChange}
              placeholder="Select date range"
              className="w-full sm:w-48"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {apiLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading transactions...</p>
          </div>
        </div>
      )}

      {apiError && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-destructive">
            <div className="h-4 w-4 rounded-full bg-destructive"></div>
            <span className="font-medium">Error loading transactions</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{apiError}</p>
          <Button variant="outline" size="sm" onClick={() => fetchTransactions()} className="mt-2">
            Try Again
          </Button>
        </div>
      )}

      {!apiLoading && !apiError && filteredTransactions.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {displayedTransactions.length} of {filteredTransactions.length} transactions
          {dateFilter === DATE_FILTER_OPTIONS.CUSTOM && customDateRange.from && (
            <span className="ml-2 text-xs">(Filtered: {getFilterDisplayText()})</span>
          )}
        </div>
      )}

      {!apiLoading &&
        !apiError &&
        filteredTransactions.length === 0 &&
        transactions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No transactions found</p>
          </div>
        )}

      {!apiLoading && !apiError && transactions.length > 0 && filteredTransactions.length === 0 && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No transactions match your search criteria</p>
        </div>
      )}

      {!apiLoading && !apiError && filteredTransactions.length > 0 && (
        <Card>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  {renderTableHeader('firstName', 'First Name', 'whitespace-nowrap')}
                  {renderTableHeader('lastName', 'Last Name', 'whitespace-nowrap')}
                  {renderTableHeader('email', 'Email')}
                  {renderTableHeader(
                    'employeeEntrataEmail',
                    'Employee Email',
                    'hidden lg:table-cell whitespace-nowrap'
                  )}
                  {renderTableHeader('points', 'Points', 'text-right')}
                  <th className="text-left py-4 px-4 font-medium hidden lg:table-cell">
                    <span>Description</span>
                  </th>
                  {renderTableHeader('rewardedBy', 'Rewarded By', 'hidden sm:table-cell')}
                  {renderTableHeader('date', 'Date')}
                </tr>
              </thead>
              <tbody>
                {displayedTransactions.length > 0 ? (
                  displayedTransactions.map(renderTableRow)
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-muted-foreground">
                      No transactions found matching your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4 p-4">
            {displayedTransactions.length > 0 ? (
              displayedTransactions.map(renderMobileCard)
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No transactions found matching your search criteria.
              </div>
            )}
          </div>
        </Card>
      )}

      {!apiLoading && !apiError && hasMoreTransactions && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={isLoading}
            className="min-w-[160px]"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"></div>
                <span>Loading...</span>
              </div>
            ) : (
              `Load More Transactions (${filteredTransactions.length - displayCount} remaining)`
            )}
          </Button>
        </div>
      )}

      {!apiLoading && !apiError && !hasMoreTransactions && displayedTransactions.length > 8 && (
        <div className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            You've reached the end of the transaction history
          </p>
        </div>
      )}
    </div>
  )
}
